/* eslint-disable no-extra-parens */
/* eslint-disable react/display-name */
import React, { memo, MouseEvent, useCallback, useEffect, useRef } from 'react';
import { reduxConnect } from '../../../common';

import { dataLayer } from '@weatherbug/common';
import { useInterval } from '../../../common/hooks/useInterval';
import { getSolarDataByLatLng } from '../../../common/actions/solarActions';
import { smallScreenMaxWidth } from '../../../common/utilities/sizeHelpers';
import { IHourlyForecastPeriod, ScrollButtonStatus, SideScrollType } from '../common/types';
import { setForecastScrollButtonStatus, setForecastSideScroll } from '../../../common/actions/forecastPageScrollActions';
import { autoscrollTo10DayHourURLParamAction, resetHourlyForecastDataAction } from '../../../common/actions/hourlyForecastPageViewActions';
import { getQueryParameterByName } from '../../../common/utilities/queryParameters';
import { HourlyForecastUIView } from './hourlyForecastUIView';
import { IStdParameters } from '../../common/interfaces/types';

const { getFullHourlyForecastV6, fullHourlyRequestParams } = dataLayer.actions;

export const requestParamsList = [
    fullHourlyRequestParams
] as const;

const solarDataCount = 7;
const locationType = 'latitudelongitude';
const verbose = false;
const refresh = false;

type GetHourlyForecastParamsType = {
    location: string;
    locationType: string;
    requestParamsList: typeof requestParamsList;
    offset?: number;
    length?: number;
    baseUrl: string;
    authId: string;
    secret: string;
    stdParameters: IStdParameters;
    verbose: boolean;
    refresh: boolean;
    temperatureUnit: number;
    pressureUnit: number;
    speedUnit: number;
    precipitationUnit: number;
}
interface HoulyForecastWrapperProps {
    latitude: number;
    longitude: number;
    forecastBaseUrl: string;
    authId: string;
    secret: string;
    stdParameters: IStdParameters;
    unitSystem: number;
    tempUnit: number;
    precipitationUnit: number;
    windUnit: number;
    pressureUnit: number;
    pageTabIsVisible: boolean;
    solarDataIsLoaded: boolean;
    hourlyPollerDelaySeconds: number;
    apiHourlyForecastPeriods: IHourlyForecastPeriod[];
    hourlyForecastPeriods: IHourlyForecastPeriod[];
    scrollButtonStatus: ScrollButtonStatus;
    sideScroll: SideScrollType;
    resetHourlyForecastDataAction(): void;
    autoscrollTo10DayHourURLParamAction(hourQueryParam: string): void;
    setForecastSideScroll(sideScrollData: SideScrollType): void;
    getAlmanacByLatLon(latitude: number, longitude: number, baseUrl: string, authId: string, secret: string): void;
    getSolarDataByLatLng(latitude: number, longitude: number, count: number): void;
    setForecastScrollButtonStatus(scrollButtonStatus: ScrollButtonStatus): void;
    getFullHourlyForecastV6(params: GetHourlyForecastParamsType): void;
}

const mapStoreStateToProps = (storeState: any) => {
    const { appState, hourlyForecastPage, forecastPageScroll, pageVisibility, units, solar } = storeState || {};
    const { hourlyForecastPeriods, apiHourlyForecastPeriods } = hourlyForecastPage || {};
    const { scrollButtonStatus, sideScroll } = forecastPageScroll || {};

    const {
        forecastBaseUrl,
        authId,
        secret,
        user,
        stdParameters,
        hourlyPollerDelaySeconds
    } = appState?.appState || {};
    const { ActiveLocation } = user || {};
    const pageTabIsVisible = pageVisibility?.visible;
    const { tempUnit, windUnit, precipitationUnit, pressureUnit, unitSystem } = units || {};

    const solarReqCompleted = solar?.solarData?.length > 0 
        ? true
        : !!solar?.lastError;

    return {
        latitude: ActiveLocation?.Locality?.Latitude,
        longitude: ActiveLocation?.Locality?.Longitude,
        forecastBaseUrl,
        hourlyPollerDelaySeconds,
        authId,
        secret,
        stdParameters,
        unitSystem,
        tempUnit,
        precipitationUnit,
        windUnit,
        pressureUnit,
        pageTabIsVisible,
        hourlyForecastPeriods,
        scrollButtonStatus,
        apiHourlyForecastPeriods,
        sideScroll,
        solarDataIsLoaded: solarReqCompleted // if solar data request was fired
    };
};

const mapDispatchToProps = {
    getFullHourlyForecastV6,
    getSolarDataByLatLng,
    setForecastScrollButtonStatus,
    setForecastSideScroll,
    resetHourlyForecastDataAction,
    autoscrollTo10DayHourURLParamAction
};

const HourlyForecastWrapper = reduxConnect(mapStoreStateToProps, mapDispatchToProps)(memo(({ 
    getFullHourlyForecastV6,
    setForecastScrollButtonStatus,
    setForecastSideScroll,
    resetHourlyForecastDataAction,
    autoscrollTo10DayHourURLParamAction,
    sideScroll,
    latitude,
    longitude,
    unitSystem = 1,
    tempUnit,
    precipitationUnit,
    windUnit,
    pageTabIsVisible = true,
    solarDataIsLoaded = false,
    pressureUnit,
    authId,
    secret,
    forecastBaseUrl,
    hourlyPollerDelaySeconds,
    apiHourlyForecastPeriods = [],
    hourlyForecastPeriods = [],
    scrollButtonStatus,
    getSolarDataByLatLng,
    stdParameters
}: HoulyForecastWrapperProps) => {

    const hasCheckedHourURLParamRef = useRef<boolean>(false);
    const serverInitialDataFlagTimeout = useRef<NodeJS.Timeout | null>(null);
    const prevProps = useRef<{
        latitude: number | null;
        longitude: number | null;
        tempUnit: number | null;
        solarDataIsLoaded?: boolean;
        unitSystem: number | null;
        pageTabIsVisible?: boolean;
        pressureUnit: number | null;
        windUnit: number | null;
    }>({
        latitude: null,
        longitude: null,
        tempUnit: null,
        unitSystem: null,
        pressureUnit: null,
        windUnit: null
    });

    const solarPrevLatLon = useRef<{
        latitude: number | null;
        longitude: number | null;
    }>({
        latitude: null,
        longitude: null
    });

    useEffect(() => {
        return () => {
            if (serverInitialDataFlagTimeout.current) {
                clearTimeout(serverInitialDataFlagTimeout.current);
            }
        };
    }, []);

    useEffect(() => {
        if (Number.isFinite(latitude) && Number.isFinite(longitude) && latitude !== solarPrevLatLon.current.latitude && longitude !== solarPrevLatLon.current.longitude) {
            if (!(window as any)?._AppData?.shouldUseServerAlmanacSolarData) {
                getSolarDataByLatLng(latitude, longitude, solarDataCount);
            }
            // eslint-disable-next-line dot-notation
            (window as any)._AppData['shouldUseServerAlmanacSolarData'] = false;
            solarPrevLatLon.current.latitude = latitude;
            solarPrevLatLon.current.longitude = longitude;
        }
    }, [latitude, longitude]);

    useEffect(() => {

        if ((window as any)._AppData?.shouldUseServerHourlyForecastData && (
            (!prevProps.current.latitude && prevProps.current.latitude !== latitude)
            && (!prevProps.current.longitude && prevProps.current.longitude !== longitude)
            && (!prevProps.current.tempUnit && prevProps.current.tempUnit !== tempUnit)
            && (!prevProps.current.pressureUnit && prevProps.current.pressureUnit !== pressureUnit)
            && (!prevProps.current.windUnit && prevProps.current.windUnit !== windUnit)
            && (!prevProps.current.solarDataIsLoaded && prevProps.current.solarDataIsLoaded !== solarDataIsLoaded)
            && (!prevProps.current.pageTabIsVisible && prevProps.current.pageTabIsVisible !== pageTabIsVisible)
            && (!prevProps.current.unitSystem && prevProps.current.unitSystem !== unitSystem)
        )) {
            // TechDebt:
            // we are using setTimeout to delay this efficiently prevent extra data fetch by some browsers
            serverInitialDataFlagTimeout.current = setTimeout(() => {
                // eslint-disable-next-line dot-notation
                (window as any)._AppData['shouldUseServerHourlyForecastData'] = false;
            }, 1000);
        } else if ((window as any)._AppData?.shouldUseServerHourlyForecastData && (
            (prevProps.current.latitude && Number.isFinite(latitude) && prevProps.current.latitude !== latitude)
            || (prevProps.current.longitude && Number.isFinite(latitude) && prevProps.current.longitude !== longitude)
            || (prevProps.current.tempUnit && prevProps.current.tempUnit !== tempUnit)
            || (prevProps.current.pressureUnit && prevProps.current.pressureUnit !== pressureUnit)
            || (prevProps.current.windUnit && prevProps.current.windUnit !== windUnit)
            || (typeof prevProps.current.solarDataIsLoaded === 'boolean' && prevProps.current.solarDataIsLoaded !== solarDataIsLoaded)
            || (typeof prevProps.current.pageTabIsVisible === 'boolean' && prevProps.current.pageTabIsVisible !== pageTabIsVisible)
            || (prevProps.current.unitSystem && prevProps.current.unitSystem !== unitSystem)
        )) {
            // eslint-disable-next-line dot-notation
            (window as any)._AppData['shouldUseServerHourlyForecastData'] = false;
        }
        
        prevProps.current.latitude = latitude;
        prevProps.current.longitude = longitude;
        prevProps.current.tempUnit = tempUnit;
        prevProps.current.pressureUnit = pressureUnit;
        prevProps.current.windUnit = windUnit;
        prevProps.current.solarDataIsLoaded = solarDataIsLoaded;
        prevProps.current.pageTabIsVisible = pageTabIsVisible;
        prevProps.current.unitSystem = unitSystem;

    }, [tempUnit, solarDataIsLoaded, unitSystem, latitude, longitude, pageTabIsVisible, pressureUnit, windUnit]);

    useInterval(
        () => {
            if (!(window as any)._AppData?.shouldUseServerHourlyForecastData) {
                getFullHourlyForecastV6({
                    location: `${latitude},${longitude}`,
                    locationType,
                    requestParamsList,
                    baseUrl: forecastBaseUrl,
                    authId,
                    secret,
                    stdParameters,
                    verbose,
                    refresh,
                    temperatureUnit: tempUnit,
                    pressureUnit,
                    speedUnit: windUnit,
                    precipitationUnit
                });
            }
        },
        hourlyPollerDelaySeconds * 1000, // delay value is in ms, convert 900 * 1000 = 900,000 => 15mins
        true,
        !!(solarDataIsLoaded && hourlyPollerDelaySeconds && authId && secret && unitSystem && forecastBaseUrl && latitude && longitude && pageTabIsVisible && tempUnit),
        [tempUnit, solarDataIsLoaded, unitSystem, latitude, longitude, pageTabIsVisible, pressureUnit, windUnit]
    );

    useEffect(() => {
        const hourQueryParam = getQueryParameterByName('hour');
        if (hourlyForecastPeriods?.length && apiHourlyForecastPeriods?.length && hourQueryParam) {
            if (!hasCheckedHourURLParamRef.current) {
                hasCheckedHourURLParamRef.current = true;
                autoscrollTo10DayHourURLParamAction(hourQueryParam);
            }
        }
    }, [hourlyForecastPeriods?.length, apiHourlyForecastPeriods?.length]);

    useEffect(() => {
        return () => {
            resetHourlyForecastDataAction();
        };
    }, []);

    const scrollForecastCardsEvent = useCallback((event: React.UIEvent<HTMLDivElement>) => {
        if (scrollButtonStatus && event.currentTarget) {
            const { isLeftScrollButtonDisabled, isRightScrollButtonDisabled } = scrollButtonStatus;
            const updatedButtonScrollStatus = { ...scrollButtonStatus };
            const { scrollLeft, scrollWidth, offsetWidth } = event.currentTarget;

            // handle update prev scroll button state
            if (scrollLeft <= 3) {
                if (!isLeftScrollButtonDisabled) {
                    updatedButtonScrollStatus.isLeftScrollButtonDisabled = true;
                }
            } else {
                if (isLeftScrollButtonDisabled) {
                    updatedButtonScrollStatus.isLeftScrollButtonDisabled = false;
                }
            }

            // handle update next scroll button state
            if (hourlyForecastPeriods?.length && apiHourlyForecastPeriods?.length) {
                const rightScrollEnd = scrollLeft + offsetWidth - scrollWidth;
                if (rightScrollEnd === 0 && scrollLeft !== 0 && offsetWidth > smallScreenMaxWidth) {
                    if (!isRightScrollButtonDisabled) {
                        updatedButtonScrollStatus.isRightScrollButtonDisabled = true;
                    }
                } else {
                    if (isRightScrollButtonDisabled) {
                        updatedButtonScrollStatus.isRightScrollButtonDisabled = false;
                    }
                }
            }
            
            // fires redux action only if there's an update since scroll callback is called multiple time. Keep this lightweight as much as possible
            if (isLeftScrollButtonDisabled !== updatedButtonScrollStatus.isLeftScrollButtonDisabled || isRightScrollButtonDisabled !== updatedButtonScrollStatus.isRightScrollButtonDisabled) {
                setForecastScrollButtonStatus(updatedButtonScrollStatus);
            }
        }
    }, [scrollButtonStatus?.isLeftScrollButtonDisabled, scrollButtonStatus?.isRightScrollButtonDisabled, apiHourlyForecastPeriods.length, hourlyForecastPeriods.length]);

    const handleMouseDown = useCallback((event: MouseEvent) => {
        const { currentTarget } = event;
        const isMouseBtnDown = true;
        const updatedSideScrollData = {
            isMouseBtnDown,
            // current scroll 
            left: currentTarget.scrollLeft,
            top: currentTarget.scrollTop,
            // current mouse position
            x: event.clientX,
            y: event.clientY
        };

        setForecastSideScroll(updatedSideScrollData);
    }, [sideScroll?.isMouseBtnDown, sideScroll.left, sideScroll.top, sideScroll.x, sideScroll.y]);

    const handleMouseLeaveOrUp = useCallback(() => {
        if (sideScroll?.isMouseBtnDown) {
            setForecastSideScroll({
                ...sideScroll,
                isMouseBtnDown: false
            });
        }
    }, [sideScroll?.isMouseBtnDown, sideScroll.x, sideScroll.y, sideScroll.left, sideScroll.top]);

    const handleMouseMove = useCallback((mouseMoveEvent: MouseEvent) => {
        if (!sideScroll?.isMouseBtnDown) {
            return;
        }
        mouseMoveEvent.preventDefault();
        const { currentTarget } = mouseMoveEvent;
        
        // How far the mouse has been moved
        const dx = mouseMoveEvent.clientX - sideScroll.x;
        const dy = mouseMoveEvent.clientY - sideScroll.y;

        // Scroll the element
        currentTarget.scrollTop = sideScroll.top - dy;
        currentTarget.scrollLeft = sideScroll.left - dx;
    }, [sideScroll?.isMouseBtnDown, sideScroll?.left, sideScroll?.x, sideScroll.top, sideScroll.y]);

    return (
        <HourlyForecastUIView 
            isMouseBtnDown={sideScroll?.isMouseBtnDown} 
            handleMouseLeaveOrUp={handleMouseLeaveOrUp}
            onMouseLeave={handleMouseLeaveOrUp}
            handleMouseMove={handleMouseMove}
            handleMouseDown={handleMouseDown} 
            scrollForecastCardsEvent={scrollForecastCardsEvent} />
    );
}));

export { HourlyForecastWrapper as default };
